import { Icons } from '@/components/icons';
import type {
  Filter,
  FilterEnumChoice,
} from '@/components/table-filters/constants';
import {
  BOOLEAN_FILTER,
  defaultBooleanFilter,
  defaultDateFilter,
  defaultEnumFilter,
  defaultNumberFilter,
  defaultRelationFilter,
  defaultStringFilter,
  NUMBER_FILTER,
  OPERATORS,
} from '@/components/table-filters/constants';
import {
  CompanyConfig,
  InvoiceConfig,
  TenantConfig,
} from '@pigello/pigello-matrix';
import baseFilters, {
  baseGlobalRealEstatesFilter,
  baseTagFilter,
} from '../../filters';

const INVOICE_STATUS_FILTERS: FilterEnumChoice[] = [
  {
    filters: [{ operator: 'noop', value: 'credit_rental_invoices' }],
    title: 'Kreditavier',
    id: 'credit_rental_invoices',
    badgeVariant: 'outline',
    overrideExternalFieldName: 'custom_invoice_filter',
  },
  {
    filters: [{ operator: 'noop', value: 'credit_invoices' }],
    title: 'Kreditfakturor',
    id: 'credit_invoices',
    badgeVariant: 'outline',
    overrideExternalFieldName: 'custom_invoice_filter',
  },
  {
    filters: [{ operator: 'noop', value: 'not_attested' }],
    title: 'Ej attesterade',
    badgeVariant: 'yellow',
    overrideExternalFieldName: 'custom_invoice_filter',
  },
  {
    filters: [{ operator: 'noop', value: 'failed_attested' }],
    title: 'Misslyckade attesteringar',
    badgeVariant: 'destructive',
    overrideExternalFieldName: 'custom_invoice_filter',
  },
  {
    filters: [
      {
        operator: 'noop',
        value: 'open',
      },
    ],
    title: 'Öppna',
    badgeVariant: 'blue',
    overrideExternalFieldName: 'custom_invoice_filter',
  },
  {
    filters: [
      {
        operator: 'noop',
        value: 'fully_paid_with_open_debt',
      },
    ],
    title: 'Slutbetalda (Öppna påminnelser/krav)',
    badgeVariant: 'orange',
    overrideExternalFieldName: 'custom_invoice_filter',
  },
  {
    filters: [
      {
        operator: 'noop',
        value: 'fully_paid',
      },
    ],
    title: 'Slutbetalda',
    badgeVariant: 'green',
    overrideExternalFieldName: 'custom_invoice_filter',
  },

  {
    filters: [
      {
        operator: 'noop',
        value: 'overdue',
      },
    ],
    title: 'Förfallna',
    badgeVariant: 'destructive',
    overrideExternalFieldName: 'custom_invoice_filter',
  },
  {
    filters: [
      {
        operator: 'noop',
        value: 'reminderspending',
      },
    ],
    title: 'Automatiska påminnelser aktivt',
    badgeVariant: 'purple',
    overrideExternalFieldName: 'custom_invoice_filter',
  },
  {
    filters: [
      {
        operator: 'noop',
        value: 'reminders',
      },
    ],
    title: 'Påminnelse skickad',
    badgeVariant: 'purple',
    overrideExternalFieldName: 'custom_invoice_filter',
  },
  {
    filters: [
      {
        operator: 'noop',
        value: 'debt',
      },
    ],
    title: 'Kravhantering aktiv',
    badgeVariant: 'purple',
    overrideExternalFieldName: 'custom_invoice_filter',
  },
];

const invoiceFilters = (
  kind: 'rental' | 'non_rental' | 'undetermined' = 'undetermined'
): Filter[] => [
  {
    ...defaultEnumFilter,
    title: 'Status',
    externalFieldName: 'custom_invoice_filter',
    enumChoices: INVOICE_STATUS_FILTERS.filter((f) => {
      if (kind === 'undetermined') return true;
      if (kind === 'rental') return f.id !== 'credit_invoices';
      if (kind === 'non_rental') return f.id !== 'credit_rental_invoices';
    }),
  },
  {
    ...defaultNumberFilter,
    title: InvoiceConfig.fields.invoiceNumber.verboseName,
    externalFieldName: InvoiceConfig.fields.invoiceNumber.externalFieldName,
    icon: Icons.number,
  },
  {
    ...defaultStringFilter,
    title: InvoiceConfig.fields.ocrNumber.verboseName,
    externalFieldName: InvoiceConfig.fields.ocrNumber.externalFieldName,
    icon: Icons.number,
  },
  {
    ...defaultDateFilter,
    title: InvoiceConfig.fields.attestedDate.verboseName,
    externalFieldName: InvoiceConfig.fields.attestedDate.externalFieldName,
    defaultOperator: OPERATORS.DATE_PAST[0],
  },
  {
    ...defaultDateFilter,
    title: InvoiceConfig.fields.closedTime.verboseName,
    externalFieldName: InvoiceConfig.fields.closedTime.externalFieldName,
    defaultOperator: OPERATORS.DATE_PAST[0],
  },
  {
    ...defaultDateFilter,
    title: InvoiceConfig.fields.invoiceDate.verboseName,
    externalFieldName: InvoiceConfig.fields.invoiceDate.externalFieldName,
    defaultOperator: OPERATORS.DATE_PAST[0],
  },
  {
    ...defaultDateFilter,
    title: InvoiceConfig.fields.dueDate.verboseName,
    externalFieldName: InvoiceConfig.fields.dueDate.externalFieldName,
  },
  {
    ...defaultDateFilter,
    title: InvoiceConfig.fields.mainPeriodStart.verboseName,
    externalFieldName: InvoiceConfig.fields.mainPeriodStart.externalFieldName,
  },
  {
    ...defaultDateFilter,
    title: InvoiceConfig.fields.mainPeriodEnd.verboseName,
    externalFieldName: InvoiceConfig.fields.mainPeriodEnd.externalFieldName,
  },
  {
    ...defaultRelationFilter,
    title: InvoiceConfig.fields.company.verboseName,
    externalFieldName: InvoiceConfig.fields.company.externalFieldName,
    relationModelName: CompanyConfig.modelName,
    icon: Icons.company,
  },
  {
    ...defaultBooleanFilter,
    title: InvoiceConfig.fields.useRounding.verboseName,
    externalFieldName: InvoiceConfig.fields.useRounding.externalFieldName,
  },
  {
    ...defaultBooleanFilter,
    title: InvoiceConfig.fields.utilizeCustomerBalance.verboseName,
    externalFieldName:
      InvoiceConfig.fields.utilizeCustomerBalance.externalFieldName,
  },
  {
    ...defaultBooleanFilter,
    title: InvoiceConfig.fields.autogiroEnabled.verboseName,
    externalFieldName: InvoiceConfig.fields.autogiroEnabled.externalFieldName,
  },
  {
    ...defaultBooleanFilter,
    title: InvoiceConfig.fields.internalInvoice.verboseName,
    externalFieldName: InvoiceConfig.fields.internalInvoice.externalFieldName,
  },
  {
    kind: BOOLEAN_FILTER,
    operators: [...OPERATORS.NOOP],
    defaultOperator: OPERATORS.NOOP[0],
    icon: Icons.boolean,
    title: 'Har misslyckats med autogirodragning',
    externalFieldName: 'has_failed_autogiro_payment',
  },
  {
    kind: BOOLEAN_FILTER,
    operators: [...OPERATORS.NOOP],
    defaultOperator: OPERATORS.NOOP[0],
    icon: Icons.boolean,
    title: 'Har väntande autogirodragning',
    externalFieldName: 'has_delivered_autogiro_payment',
  },
  {
    kind: BOOLEAN_FILTER,
    operators: [...OPERATORS.NOOP],
    defaultOperator: OPERATORS.NOOP[0],
    icon: Icons.boolean,
    title: 'Har lyckad autogirodragning',
    externalFieldName: 'has_successful_autogiro_payment',
  },
  {
    kind: BOOLEAN_FILTER,
    operators: [...OPERATORS.NOOP],
    defaultOperator: OPERATORS.NOOP[0],
    icon: Icons.boolean,
    title: 'Automatiska påminnelser pausade',
    externalFieldName: 'automatic_debt_invoicing_paused',
  },
  {
    title: 'Originalbelopp brutto (SEK)',
    externalFieldName: 'total_value',
    kind: NUMBER_FILTER,
    operators: [...OPERATORS.NOOP, ...OPERATORS.LTE, ...OPERATORS.GTE],
    defaultOperator: OPERATORS.NOOP[0],
    icon: Icons.fee,
  },
  {
    ...defaultRelationFilter,
    title: 'Hyresgäst',
    relationModelName: TenantConfig.modelName,
    externalFieldName: 'tenant',
    operators: ['noop'],
    defaultOperator: 'noop',
    icon: Icons.tenant,
  },
  ...baseGlobalRealEstatesFilter,
  ...baseTagFilter,
  ...baseFilters,
];

export default invoiceFilters;
